<template>
  <div class="text-left">
    <RejectCommentLayout :comment-text="reject_comment" />
    <v-form ref="form">
      <v-stepper v-model="step">
        <v-stepper-header class="elevation-0">
          <v-stepper-step :complete="step > 1" step="1">{{
            $t("project.basicData")
          }}</v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step step="2" :complete="step > 2">{{
            $t("project.consortiumData")
          }}</v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step step="3" :complete="step > 3">{{
            $t("project.subprojectsData")
          }}</v-stepper-step>

          <v-divider></v-divider>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">

            <v-row>
              <v-col cols="12" class="py-0">
                <MultilingualTextField v-if="load_complete" v-model="project.title" :label="$t('form.project.title')" entity-type="project" field="title" :readonly="disabledForm" />
              </v-col>
            </v-row>
            <h5>
              {{ $t("form.project.acronym") }}
            </h5>
            <v-row>
              <v-col class="py-0">
                <v-text-field
                  v-model="project.acronym"
                  :label="$t('form.project.acronym')"
                  :readonly="disabledForm"
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row class="mt-0">
              <v-col cols="12" sm="6" class="pt-0">
                <h5>
                  {{ $t("type") }}
                </h5>
                <v-row>
                  <v-col class="py-0">
                    <v-select
                      v-model="project.type"
                      :items="projectTypes"
                      item-value="value"
                      item-text="text"
                      :label="$t('type')"
                      outlined
                      :readonly="disabledForm"
                      :rules="rules.fieldRequired"
                    ></v-select>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" sm="6" class="pt-0">
                <h5>
                  {{ $t("form.project.external") }}
                </h5>
                <v-row>
                  <v-col class="py-0">
                    <v-checkbox
                      v-model="project.external"
                      :label="$t('form.project.external_more')"
                      :readonly="disabledForm"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6">
                <h5>
                  {{ $t('form.doi') }}
                </h5>
                <v-row>
                  <v-col cols="12" class="py-0">
                    <v-text-field
                      v-model="project.doi"
                      :label="$t('form.doi')"
                      outlined
                      :rules="rules.doiRules"
                      :readonly="disabledForm"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="6">
                <h5>
                  {{ $t('form.projectcode') }}
                </h5>
                <v-row>
                  <v-col cols="12" class="py-0">
                    <v-text-field
                      v-model="project.code"
                      :label="$t('form.projectcode')"
                      outlined
                      :rules="rules.projectcodeRules"
                      :readonly="disabledForm"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <h5>
              {{ $t("form.project.dates") }}
            </h5>
            <v-row>
              <v-col cols="6" class="py-0">
                <v-menu
                  v-model="menu_start_date"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  :disabled="disabledForm"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="project.start_date"
                      :label="$t('form.project.start_date')"
                      prepend-inner-icon="event"
                      readonly
                      outlined
                      v-on="on"
                      :rules="rules.fieldRequired"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="project.start_date"
                    @input="menu_start_date = false"
                    :readonly="disabledForm"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="6" class="py-0">
                <v-menu
                  v-model="menu_end_date"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  :disabled="disabledForm"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="project.end_date"
                      :label="$t('form.project.end_date')"
                      prepend-inner-icon="event"
                      readonly
                      outlined
                      v-on="on"
                      :rules="rules.fieldRequired"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="project.end_date"
                    :min="project.start_date"
                    @input="menu_end_date = false"
                    :readonly="disabledForm"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-0">
                <MultilingualTextField v-if="load_complete" v-model="project.description" :label="$t('form.description')" entity-type="project" field="description" rich :readonly="disabledForm" />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-0">
                <MultilingualTextField v-if="load_complete" v-model="project.objectives" :label="$t('form.project.objectives')" entity-type="project" field="objectives" rich :readonly="disabledForm" />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-0">
                <MultilingualTextField v-if="load_complete" v-model="project.results" :label="$t('form.project.results')" entity-type="project" field="results" rich :readonly="disabledForm" />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <h5>{{ $t("form.project.main_citius_investigators") }}</h5>
                <SelectPeopleList
                  :disabled-form="disabledForm"
                  :items-selected="project.main_citius_investigators"
                />
              </v-col>
              <v-col cols="12" md="6">
                <h5>{{ $t("form.project.citius_investigators") }}</h5>
                <SelectPeopleList
                  :disabled-form="disabledForm"
                  :items-selected="project.citius_investigators"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <h5>{{ $t("form.project.main_external_investigators") }}</h5>
                <v-row>
                  <v-col>
                    <ArrayFieldForm
                      v-model="project.main_external_investigators"
                      :read-only="disabledForm"
                      name-field="main_external_investigators"
                      :label="$t('form.project.main_external_investigators')"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" md="6">
                <h5>{{ $t("form.project.external_investigators") }}</h5>
                <v-row>
                  <v-col>
                    <ArrayFieldForm
                      v-model="project.external_investigators"
                      :read-only="disabledForm"
                      name-field="external_investigators"
                      :label="$t('form.project.external_investigators')"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <h5>{{ $t("form.files") }}</h5>
            <v-alert
              v-if="modOp_id === null"
              dense
              text
              type="info"
              class="without-margin-bottom"
            >
              {{ $t("form.publication.files.info") }}
            </v-alert>
            <v-card min-height="200px" elevation="0">
              <UploadFiles
                :read-only="disabledForm"
                object_type="project"
                :id="this.modOp_id"
                :final="false"
              />
              <v-row>
                <v-overlay
                  :opacity="0.85"
                  :absolute="true"
                  :value="modOp_id === null"
                  color="#ffffff"
                >
                  <v-btn
                    color="secondary"
                    @click="saveform_trigger = !saveform_trigger"
                  >
                    {{ $t("button.saveForm") }}
                  </v-btn>
                </v-overlay>
              </v-row>
            </v-card>
            <v-row>
              <v-col>
                <h5>
                  {{ $t("form.project.funder_programs") }}
                </h5>
                <v-row>
                  <v-col>
                    <SelectPeopleList
                      :disabled-form="disabledForm"
                      :items-selected="project.funder_programs"
                      endpoint="funderProgram"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <MultilingualTextField v-if="load_complete" v-model="project.funder_text" :label="$t('form.funder_text')" entity-type="project" field="funder_text" rich :readonly="disabledForm" />
            <ProjectBudgetFields :total-budget.sync="project.total_budget" :citius-budget.sync="project.citius_budget" :readonly="disabledForm" />
          <v-row>
            <v-col>
              <h5>
                {{ $t("form.project.additional_text") }}
              </h5>
              <v-text-field
                v-model="project.additional_text"
                :label="$t('form.project.additional_text')"
                :readonly="disabledForm"
                outlined
              ></v-text-field>
            </v-col>
          </v-row>
            <v-row>
              <v-col>
                <h5>
                  {{ $t("form.project.contractor") }}
                </h5>
                <v-autocomplete
                  v-model="project.contractor"
                  :label="$t('form.project.contractor')"
                  :readonly="disabledForm"
                  :items="organizations"
                  item-value="id"
                  item-text="name"
                  return-object
                  outlined
                ></v-autocomplete>
              </v-col>
            </v-row>
            <h5>
              {{ $t("form.project.image") }}
            </h5>
            <UploadImage
              :path="
                $store.state.endpoint +
                  '/user/moderatedOperations/project/' +
                  $route.params.id
              "
              :url="$store.state.endpoint + project.image"
              :object="{ name: project.image }"
              :object_created="$route.params.id !== undefined"
              name_field="image"
            />
            <v-row>
              <v-col>
                <h5>
                  {{ $t("form.project.uri") }}
                </h5>
                <v-row>
                  <v-col class="py-0">
                    <v-text-field
                      v-model="project.uri"
                      :label="$t('form.project.uri')"
                      :readonly="disabledForm"
                      :rules="rules.uriRules"
                      outlined
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <h5>
              {{ $t("form.publication.research_areas") }}
            </h5>
            <v-row>
              <v-col cols="12" class="py-0">
                <v-select
                  :items="researchAreas"
                  v-model="project.research_areas"
                  :label="$t('form.publication.research_areas')"
                  required
                  :item-text="'name.' + $i18n.locale"
                  item-value="id"
                  :readonly="disabledForm"
                  multiple
                  :rules="rules.fieldRequired"
                  outlined
                ></v-select>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6" class="text-left my-6">
                <v-btn class="mr-4" v-if="step > 1" @click="step = step - 1">
                  {{ $t("button.previous") }}
                </v-btn>
              </v-col>
              <v-col cols="6" class="text-right my-6">
                <v-btn class="mr-4" v-if="step < 3" @click="step = step + 1">
                  {{ $t("button.next") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>

          <v-stepper-content step="2">
            <v-row>
              <v-col>
                <v-checkbox
                  v-model="has_consortium"
                  :label="$t('form.project.has_consortium')"
                  :readonly="disabledForm"
                />
              </v-col>
            </v-row>
            <div v-if="has_consortium">
              <h5>
                {{ $t("form.project.consortium_dates") }}
              </h5>
              <v-row>
                <v-col cols="6" class="py-0">
                  <v-menu
                    v-model="menu_consortium_start_date"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    :disabled="disabledForm"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="project.consortium.start_date"
                        :label="$t('form.project.start_date')"
                        prepend-inner-icon="event"
                        readonly
                        outlined
                        v-on="on"
                        :rules="rules.fieldRequired"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="project.consortium.start_date"
                      @input="menu_consortium_start_date = false"
                      :readonly="disabledForm"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6" class="py-0">
                  <v-menu
                    v-model="menu_consortium_end_date"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    :disabled="disabledForm"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="project.consortium.end_date"
                        :label="$t('form.project.end_date')"
                        prepend-inner-icon="event"
                        readonly
                        outlined
                        v-on="on"
                        :rules="rules.fieldRequired"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="project.consortium.end_date"
                      :min="project.consortium.start_date"
                      @input="menu_consortium_end_date = false"
                      :readonly="disabledForm"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="py-0">
                  <v-checkbox
                    indeterminate
                    v-model="project.consortium.is_citius"
                    :label="$t('form.project.consortium_citius')"
                    :readonly="disabledForm"
                    :rules="rules.checkboxRequired"
                  />
                </v-col>
              </v-row>
              <h5>
                {{ $t("form.project.leader") }}
              </h5>
              <v-row>
                <v-col class="py-0">
                  <v-combobox
                    v-model="leaderSelected"
                    :items="organizations"
                    item-text="name"
                    :label="$t('form.project.leader')"
                    return-object
                    :clearable="!disabledForm"
                    outlined
                    :readonly="disabledForm"
                    :rules="rules.fieldRequired"
                  />
                </v-col>
              </v-row>
              <h5>
                {{ $t("form.project.funder_programs") }}
              </h5>
              <v-row>
                <v-col>
                  <SelectPeopleList
                    :disabled-form="disabledForm"
                    :items-selected="project.consortium.partners"
                    endpoint="organization"
                  />
                </v-col>
              </v-row>
            </div>
            <v-row>
              <v-col cols="6" class="text-left my-6">
                <v-btn class="mr-4" v-if="step > 1" @click="step = step - 1">
                  {{ $t("button.previous") }}
                </v-btn>
              </v-col>
              <v-col cols="6" class="text-right my-6">
                <v-btn class="mr-4" v-if="step < 3" @click="step = step + 1">
                  {{ $t("button.next") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>

          <v-stepper-content step="3">
            <div
              v-for="(subproject, index) in project.subprojects"
              :key="index"
            >
              <h4 class="text-left mb-5">
                {{ $t("form.project.subproject") }} {{ index + 1 }}
              </h4>

              <v-row>
                <v-col cols="12" class="py-0">
                  <MultilingualTextField v-if="load_complete" v-model="subproject.title" :label="$t('form.project.title')" entity-type="project" field="subproject_title" :readonly="disabledForm" />
                </v-col>
              </v-row>
              <h5>
                {{ $t("form.project.subproject_dates") }}
              </h5>
              <v-row>
                <v-col cols="6" class="py-0">
                  <v-menu
                    v-model="tabs_subprojects[index].menu_start_date"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    :disabled="disabledForm"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="subproject.start_date"
                        :label="$t('form.project.start_date')"
                        prepend-inner-icon="event"
                        readonly
                        outlined
                        v-on="on"
                        :rules="rules.fieldRequired"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="subproject.start_date"
                      @input="tabs_subprojects[index].menu_start_date = false"
                      :readonly="disabledForm"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6" class="py-0">
                  <v-menu
                    v-model="tabs_subprojects[index].menu_end_date"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    :disabled="disabledForm"
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="subproject.end_date"
                        :label="$t('form.project.end_date')"
                        prepend-inner-icon="event"
                        readonly
                        outlined
                        v-on="on"
                        :rules="rules.fieldRequired"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="subproject.end_date"
                      :min="subproject.start_date"
                      @input="tabs_subprojects[index].menu_end_date = false"
                      :readonly="disabledForm"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>


              <v-row>
                <v-col cols="12" class="py-0">
                  <MultilingualTextField v-if="load_complete" v-model="subproject.description" :label="$t('form.description')" entity-type="project" field="subproject_description" rich :readonly="disabledForm" />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <h5>{{ $t("form.project.main_citius_investigators") }}</h5>
                  <v-autocomplete
                    v-model="subproject.main_citius_investigators"
                    :items="namePersons"
                    item-value="id"
                    item-text="name"
                    :readonly="disabledForm"
                    outlined
                    multiple
                    :label="$t('form.project.main_citius_investigators')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="6">
                  <h5>{{ $t("form.project.citius_investigators") }}</h5>
                  <v-autocomplete
                    v-model="subproject.citius_investigators"
                    :items="namePersons"
                    item-value="id"
                    item-text="name"
                    :readonly="disabledForm"
                    outlined
                    multiple
                    :label="$t('form.project.citius_investigators')"
                  ></v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <h5>{{ $t("form.project.main_external_investigators") }}</h5>
                  <v-row>
                    <v-col>
                      <ArrayFieldForm
                        v-model="subproject.main_external_investigators"
                        :read-only="disabledForm"
                        name-field="main_external_investigators"
                        :label="$t('form.project.main_external_investigators')"
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="12" md="6">
                  <h5>{{ $t("form.project.external_investigators") }}</h5>
                  <v-row>
                    <v-col>
                      <ArrayFieldForm
                        v-model="subproject.external_investigators"
                        :read-only="disabledForm"
                        name-field="external_investigators"
                        :label="$t('form.project.external_investigators')"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <h5>
                    {{ $t("form.project.contractor") }}
                  </h5>
                  <v-autocomplete
                    v-model="subproject.contractor"
                    :read-only="disabledForm"
                    :label="$t('form.project.contractor')"
                    :items="organizations"
                    item-value="id"
                    item-text="name"
                    return-object
                    outlined
                  ></v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="text-right">
                  <v-btn
                    rounded
                    outlined
                    color="red"
                    class="ma-2"
                    @click="deleteSubproject(index)"
                  >
                    {{ $t("button.deleteSubproject") }}
                  </v-btn>
                </v-col>
              </v-row>
              <v-divider />
            </div>
            <v-row>
              <v-col>
                <v-btn
                  rounded
                  outlined
                  color="primary"
                  class="ma-2 white--text"
                  v-if="!disabledForm"
                  @click="addSubproject"
                >
                  <v-icon dark>
                    mdi-plus
                  </v-icon>
                  {{ $t("button.addSubproject") }}
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6" class="text-left my-6">
                <v-btn class="mr-4" v-if="step > 1" @click="step = step - 1">
                  {{ $t("button.previous") }}
                </v-btn>
              </v-col>
              <v-col cols="6" class="text-right my-6">
                <v-btn class="mr-4" v-if="step < 3" @click="step = step + 1">
                  {{ $t("button.next") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

      <UserFormActions
        form_name_text="New project"
        :form_name="form_name"
        :refForm="$refs.form"
        :saveform_trigger="saveform_trigger"
        :object_type="object_type"
        :data="processData"
        :state="state"
        @change-state="changeState"
        :paginated-form="true"
        class="text-center mt-6"
      />
    </v-form>
  </div>
</template>

<script>
import axios from "axios";
import RejectCommentLayout from "../../layouts/RejectCommentLayout";
import ArrayFieldForm from "../../reusables/ArrayFieldForm";
import UserFormActions from "../../action_buttons/UserFormActions";
import SelectPeopleList from "../../reusables/SelectPeopleList";
import changeState from "../../../mixins/changeState";
import validationRules from "../../../mixins/validationRules";
import project_data from "../../../mixins/project_data";
import project_model from "../../../mixins/model_utils/project_model";
import UploadImage from "../../layouts/UploadImage";
import UploadFiles from "../../reusables/UploadFiles";
import MultilingualTextField from "@/components/reusables/MultilingualTextField.vue";
import ProjectBudgetFields from "@/components/projects/ProjectBudgetFields.vue";

export default {
  mixins: [changeState, validationRules, project_data, project_model],
  components: {
    UploadFiles,
    UploadImage,
    RejectCommentLayout,
    ArrayFieldForm,
    SelectPeopleList,
    MultilingualTextField,
    ProjectBudgetFields,
    UserFormActions
  },

  data: () => ({
    form_name: "newProjectForm",
    step: 1,
    tab: null,
    modOp_id: null,
    saveform_trigger: false,
    tab_title: 0,
    tab_description: 0,
    tab_objectives: 0,
    tab_results: 0,
    menu_start_date: false,
    menu_end_date: false,
    menu_consortium_start_date: false,
    menu_consortium_end_date: false,
    state: "",
    reject_comment: "",
    object_id: null,
    object_type: "project"
  }),

  created() {
    this.getResearchAreas();
    this.getNamePersons();
    this.getProjectTypes();
    this.getOrganizations();
    if (this.updateProjectForm) {
      this.getData();
    } else {
      if (this.$store.getters.user !== null) {
        this.loadDefaultMainInvestigator();
      }
      this.load_complete = true;
    }
  },

  computed: {
    updateProjectForm() {
      return this.$route.params.id > -1 && !this.$route.meta.update;
    },
    disabledForm() {
      let states = ["VD", "PA", "PV", "PC"];
      return states.includes(this.state);
    },
    processData() {
      let title = "";
      if (
        this.project.title["gl"] !== "" &&
        this.project.title["gl"] !== undefined
      ) {
        title = this.project.title["gl"];
      } else if (
        this.project.title["es"] !== "" &&
        this.project.title["es"] !== undefined
      ) {
        title = this.project.title["es"];
      } else if (
        this.project.title["en"] !== "" &&
        this.project.title["en"] !== undefined
      ) {
        title = this.project.title["en"];
      }
      let leader = "";
      if (this.leaderSelected !== undefined && this.leaderSelected !== null) {
        if (typeof this.leaderSelected == "string") {
          leader = {
            id: null,
            name: this.leaderSelected
          };
        } else if (this.leaderSelected.id != null) {
          leader = this.leaderSelected.id;
        } else {
          leader = this.leaderSelected;
        }
      } else {
        leader = null;
      }
      let data = {
        title: title,
        object_type: this.object_type,
        form_name: this.form_name,
        object_id: this.object_id,
        data: {
          user: {
            project: {
              title: this.project.title,
              description: this.project.description,
              start_date: this.project.start_date,
              end_date: this.project.end_date,
              doi: this.project.doi,
              code: this.project.code,
              objectives: this.project.objectives,
              results: this.project.results,
              acronym:
                this.project.acronym === "" ? null : this.project.acronym,
              type: this.project.type,
              external: this.project.external,
              main_citius_investigators: this.project.main_citius_investigators,
              citius_investigators: this.project.citius_investigators,
              main_external_investigators: this.project
                .main_external_investigators,
              external_investigators: this.project.external_investigators,
              funder_programs: this.project.funder_programs,
              funder_text: this.project.funder_text,
              total_budget: this.project.total_budget === '' ? null : this.project.total_budget,
              citius_budget: this.project.citius_budget === '' ? null : this.project.citius_budget,
              additional_text: this.project.additional_text,
              subprojects: this.project.subprojects,
              uri: this.project.uri,
              research_areas: this.project.research_areas.filter(area =>
                Number.isInteger(area)
              )
            }
          }
        }
      };

      if (this.has_consortium) {
        data.data.user.project["consortium"] = this.project.consortium;
        data.data.user.project["consortium"]["leader"] = leader;
      }

      return data;
    }
  },

  methods: {
    loadLeaderSelected(item) {
      if (item !== undefined) {
        if (typeof item == "number") {
          this.leaderSelected = this.organizations.filter(
            organization => organization.id === item
          )[0];
        } else {
          this.leaderSelected = item;
        }
      }
    },
    getData() {
      let path =
        this.$store.state.endpoint +
        "/user/moderatedOperations/project/" +
        this.$route.params.id;
      axios
        .get(path)
        .then(response => {
          this.modOp_id = response.data.id;
          this.project = response.data.data.user.project;
          this.addEmptyDataMultiLanguage();
          if (this.project.consortium === undefined) {
            this.project["consortium"] = this.consortium;
          } else {
            this.has_consortium = true;
            this.loadLeaderSelected(this.project.consortium.leader);
          }
          this.state = response.data.state;
          this.reject_comment = response.data.reject_comment;
          this.project.id = response.data.object_id;
          this.project.image = response.data.image;
          this.project.subprojects.forEach(subproject => {
            this.tabs_subprojects.push({
              tab_title: 0,
              tab_description: 0,
              menu_start_date: false,
              menu_end_date: false
            });
          });
        })
        .finally(() => (this.load_complete = true))
        .catch(error => {
          console.log(error);
        });
    },

    addSubproject() {
      this.tabs_subprojects.push({
        tab_title: 0,
        tab_description: 0,
        menu_start_date: false,
        menu_end_date: false
      });
      this.project.subprojects.push(
        JSON.parse(JSON.stringify(this.subproject))
      );
    },

    deleteSubproject(index) {
      this.tabs_subprojects.splice(index, 1);
      this.project.subprojects.splice(index, 1);
    },
    changeContent(lan, content, field) {
      this.project[field][lan] = content;
    },
    changeSubprojectContent(lan, content, field, index) {
      this.project.subprojects[index][field][lan] = content;
    }
  }
};
</script>
